// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import { Component, type ErrorInfo, type ReactNode } from 'react';
import logger from '../utils/logger';
import { reportError } from '../utils/errorReporting';

type Props = {
  children: ReactNode;
  fallback?: ReactNode;
};

type State = {
  hasError: boolean;
};

/**
 * Catches rendering errors in child components and displays a fallback UI
 * instead of unmounting the entire application tree.
 */
export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    logger.error('ErrorBoundary caught an error', error, { component: 'ErrorBoundary', componentStack: info.componentStack });
    reportError(error, { source: 'ErrorBoundary', componentStack: info.componentStack ?? undefined });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="flex min-h-[16rem] flex-col items-center justify-center gap-4 p-6 text-center">
          <h2 className="text-lg font-semibold text-slate-900">Something went wrong</h2>
          <p className="text-sm text-slate-600">An unexpected error occurred. Please try refreshing the page.</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Refresh page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
